// Asset Audit Management Module - Type Definitions
// This file contains all TypeScript interfaces and const objects for the Asset Audit redesign

// ============================================================================
// CONST OBJECTS (replacing enums for erasableSyntaxOnly compatibility)
// ============================================================================

export const AuditType = {
  FullInventory: 'FullInventory',
  LocationVerification: 'LocationVerification',
  ConditionAssessment: 'ConditionAssessment',
  ComplianceCheck: 'ComplianceCheck',
  HighValueAssets: 'HighValueAssets',
  Reconciliation: 'Reconciliation',
  SpotCheck: 'SpotCheck',
  // Additional types used in UI
  Physical: 'Physical',
  Compliance: 'Compliance',
  Financial: 'Financial',
  Condition: 'Condition',
  Verification: 'Verification'
} as const;
export type AuditType = (typeof AuditType)[keyof typeof AuditType];

export const AuditStatus = {
  Draft: 'Draft',
  Assigned: 'Assigned',
  InProgress: 'InProgress',
  UnderReview: 'UnderReview',
  OnHold: 'OnHold',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
  Scheduled: 'Scheduled'
} as const;
export type AuditStatus = (typeof AuditStatus)[keyof typeof AuditStatus];

export const AuditPriority = {
  Critical: 'Critical',
  High: 'High',
  Medium: 'Medium',
  Low: 'Low'
} as const;
export type AuditPriority = (typeof AuditPriority)[keyof typeof AuditPriority];

export const AuditScope = {
  AllAssets: 'AllAssets',
  Locations: 'Locations',
  Departments: 'Departments',
  Categories: 'Categories',
  CustomList: 'CustomList'
} as const;
export type AuditScope = (typeof AuditScope)[keyof typeof AuditScope];

export const AssigneeStatus = {
  Pending: 'Pending',
  Accepted: 'Accepted',
  InProgress: 'InProgress',
  Completed: 'Completed',
  Declined: 'Declined'
} as const;
export type AssigneeStatus = (typeof AssigneeStatus)[keyof typeof AssigneeStatus];

export const RecurrenceFrequency = {
  Daily: 'Daily',
  Weekly: 'Weekly',
  Monthly: 'Monthly',
  Quarterly: 'Quarterly',
  Yearly: 'Yearly'
} as const;
export type RecurrenceFrequency = (typeof RecurrenceFrequency)[keyof typeof RecurrenceFrequency];

export const ExceptionType = {
  LocationMismatch: 'LocationMismatch',
  ConditionIssue: 'ConditionIssue',
  NotFound: 'NotFound',
  DataDiscrepancy: 'DataDiscrepancy',
  MaintenanceRequired: 'MaintenanceRequired',
  ComplianceIssue: 'ComplianceIssue',
  UnauthorizedMovement: 'UnauthorizedMovement',
  DuplicateAsset: 'DuplicateAsset',
  Other: 'Other',
  // Additional types used in UI
  AssetMissing: 'AssetMissing',
  AssetDamaged: 'AssetDamaged',
  ConditionMismatch: 'ConditionMismatch'
} as const;
export type ExceptionType = (typeof ExceptionType)[keyof typeof ExceptionType];

export const ExceptionSeverity = {
  Critical: 'Critical',
  High: 'High',
  Medium: 'Medium',
  Low: 'Low'
} as const;
export type ExceptionSeverity = (typeof ExceptionSeverity)[keyof typeof ExceptionSeverity];

export const ExceptionStatus = {
  Open: 'Open',
  UnderReview: 'UnderReview',
  ActionRequired: 'ActionRequired',
  Resolved: 'Resolved',
  Closed: 'Closed',
  Escalated: 'Escalated',
  // Additional status used in UI
  InProgress: 'InProgress'
} as const;
export type ExceptionStatus = (typeof ExceptionStatus)[keyof typeof ExceptionStatus];

export const ExceptionPriority = {
  Urgent: 'Urgent',
  High: 'High',
  Medium: 'Medium',
  Low: 'Low'
} as const;
export type ExceptionPriority = (typeof ExceptionPriority)[keyof typeof ExceptionPriority];

export const AssetCondition = {
  Excellent: 'Excellent',
  Good: 'Good',
  Fair: 'Fair',
  Poor: 'Poor',
  Damaged: 'Damaged',
  Obsolete: 'Obsolete',
  // Additional condition used in UI
  Critical: 'Critical'
} as const;
export type AssetCondition = (typeof AssetCondition)[keyof typeof AssetCondition];

export const AuditActivityType = {
  Created: 'Created',
  Assigned: 'Assigned',
  Started: 'Started',
  AssetAudited: 'AssetAudited',
  ExceptionRaised: 'ExceptionRaised',
  ExceptionResolved: 'ExceptionResolved',
  CommentAdded: 'CommentAdded',
  StatusChanged: 'StatusChanged',
  UserAdded: 'UserAdded',
  UserRemoved: 'UserRemoved',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
  Rescheduled: 'Rescheduled',
  Paused: 'Paused',
  Resumed: 'Resumed'
} as const;
export type AuditActivityType = (typeof AuditActivityType)[keyof typeof AuditActivityType];

export const NotificationType = {
  AuditAssigned: 'AuditAssigned',
  AuditDueSoon: 'AuditDueSoon',
  AuditOverdue: 'AuditOverdue',
  AuditCompleted: 'AuditCompleted',
  ExceptionRaised: 'ExceptionRaised',
  ExceptionResolved: 'ExceptionResolved',
  CommentMention: 'CommentMention',
  StatusChanged: 'StatusChanged',
  AuditCancelled: 'AuditCancelled'
} as const;
export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];

// ============================================================================
// USER & ASSET TYPES (for audit wizard components)
// ============================================================================

export interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
  department?: string;
  avatar?: string;
}

export interface Asset {
  id: string;
  code: string;
  name: string;
  assetTag?: string; // Asset tag for identification
  description?: string;
  category?: string;
  categoryId?: string;
  location?: string;
  locationId?: string;
  department?: string;
  departmentId?: string;
  status?: string;
  condition?: AssetCondition;
  value?: number;
  imageUrl?: string; // Optional image URL for display
}

// ============================================================================
// CORE INTERFACES
// ============================================================================

export interface AuditAssignee {
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  role?: string;
  department?: string;
  assignedDate: string;
  status: AssigneeStatus;
  completedAssets: number;
  totalAssets?: number; // Total assets assigned to this user
  notificationSent: boolean;
  lastActivityDate?: string;
}

export interface RecurrencePattern {
  frequency: RecurrenceFrequency;
  interval: number; // e.g., every 2 weeks
  dayOfWeek?: number; // 0-6 for weekly
  dayOfMonth?: number; // 1-31 for monthly
  monthOfYear?: number; // 1-12 for yearly
  endDate?: string | null;
  occurrences?: number | null;
}

export interface ScopeDetails {
  locationIds?: string[];
  locations?: string[];
  departmentIds?: string[];
  departments?: string[];
  categoryIds?: string[];
  categories?: string[];
  assetIds?: string[];
  assets?: string[];
}

export interface Audit {
  id: string;
  auditNumber: string;
  title: string;
  name?: string; // Alias for title used in some UI components
  description?: string;
  type: AuditType;
  status: AuditStatus;
  priority: AuditPriority;
  
  // Scheduling
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  startDate?: string; // Alias for scheduledStartDate used in some UI components
  dueDate?: string; // Due date for the audit
  
  // Scope
  scope: AuditScope;
  scopeDetails: ScopeDetails;
  
  // Assignment
  assignedUsers?: AuditAssignee[];
  assignees?: AuditAssignee[]; // Alias for assignedUsers used in some UI components
  
  // Progress
  totalAssets: number;
  completedAssets: number;
  pendingAssets: number;
  exceptionCount: number;
  completionPercentage: number;
  flaggedAssets?: number;
  missingAssets?: number;
  
  // Recurrence
  isRecurring: boolean;
  recurrencePattern?: RecurrencePattern;
  parentAuditId?: string; // For recurring audit instances
  seriesId?: string; // Links all audits in a recurring series
  
  // Metadata
  createdBy: string;
  createdByName: string;
  createdDate: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
  
  // Calculated fields
  daysRemaining?: number;
  isOverdue: boolean;
  estimatedCompletionDate?: string;
  
  // Settings
  allowPartialCompletion?: boolean;
  requirePhotos?: boolean;
  requireNotes?: boolean;
  autoResolveExceptions?: boolean;
}

export interface AuditException {
  id: string;
  auditId: string;
  auditNumber: string;
  assetId: string;
  assetCode: string;
  assetName: string;
  
  // Exception details
  exceptionType: ExceptionType;
  type?: ExceptionType; // Alias for exceptionType used in some UI components
  exceptionNumber?: string; // Exception number for display
  title?: string; // Title for the exception
  description?: string; // Description of the exception
  location?: string; // Location where exception was found
  severity: ExceptionSeverity;
  status: ExceptionStatus;
  priority: ExceptionPriority;
  
  // Field-specific
  field?: string;
  expectedValue?: string;
  actualValue?: string;
  
  // Location-specific (for LocationMismatch)
  expectedLocation?: string;
  actualLocation?: string;
  expectedLocationId?: string;
  actualLocationId?: string;
  
  // Condition-specific (for ConditionIssue)
  expectedCondition?: AssetCondition;
  actualCondition?: AssetCondition;
  
  // Details
  notes?: string;
  images?: AuditImage[];
  attachments?: AuditImage[]; // Alias for images used in some UI components
  
  // Reporter
  reportedBy: string;
  reportedByName: string;
  reportedByEmail?: string;
  reportedByAvatar?: string;
  reportedDate: string;
  createdDate?: string; // Alias for reportedDate used in some UI components
  lastModifiedDate?: string; // Last modification date
  
  // Follow-up
  requiresFollowUp: boolean;
  followUpDueDate?: string;
  assignedTo?: string;
  assignedToName?: string;
  
  // Resolution
  resolvedBy?: string;
  resolvedByName?: string;
  resolvedDate?: string;
  resolutionNotes?: string;
  resolutionAction?: string;
  
  // Work order (if applicable)
  workOrderId?: string;
  workOrderNumber?: string;
  
  // Comments
  comments?: AuditComment[];
}

export interface AuditImage {
  id: string;
  url: string;
  thumbnailUrl: string;
  fileName: string;
  fileSize: number;
  mimeType?: string;
  fileType?: string; // Alias for mimeType used in some UI components
  uploadedDate: string;
  uploadedBy: string;
  uploadedByName: string;
  caption?: string;
  width?: number;
  height?: number;
}

export interface AuditActivityLog {
  id: string;
  auditId: string;
  activityType: AuditActivityType;
  description: string;
  userId: string;
  userName: string;
  performedBy?: string; // Alias for userName used in some UI components
  userAvatar?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface AuditComment {
  id: string;
  auditId: string;
  exceptionId?: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  comment: string;
  mentions?: string[]; // User IDs mentioned
  attachments?: AuditImage[];
  createdDate: string;
  lastModifiedDate?: string;
  isEdited: boolean;
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  link?: string;
  isRead: boolean;
  createdDate: string;
  priority: 'high' | 'medium' | 'low';
  auditId?: string;
  auditNumber?: string;
  exceptionId?: string;
  userId: string;
}

// ============================================================================
// DASHBOARD & METRICS
// ============================================================================

export interface DashboardMetrics {
  activeAuditsCount: number;
  activeAuditsBreakdown?: {
    assigned: number;
    inProgress: number;
    underReview: number;
    onHold: number;
  };
  openExceptionsCount: number;
  openExceptionsBySeverity?: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  overdueAuditsCount: number;
  overdueAudits?: OverdueAudit[];
  completionRate?: {
    percentage: number;
    trend: number; // +/- percentage vs previous period
    sparklineData: number[]; // Last 30 days
  };
  auditActivityTimeline?: AuditActivityTimelineData[];
  exceptionDistribution?: ExceptionDistributionData[];
}

export interface OverdueAudit {
  auditNumber: string;
  title: string;
  daysOverdue: number;
  assignee: string;
  priority: AuditPriority;
}

export interface AuditActivityTimelineData {
  date: string;
  started: number;
  completed: number;
  exceptions: number;
}

export interface ExceptionDistributionData {
  type: ExceptionType;
  count: number;
  percentage: number;
}

export interface HistoricalAnalytics {
  totalAudits: number;
  completedAudits: number;
  cancelledAudits: number;
  averageCompletionTime: number; // in days
  totalAssetsAudited: number;
  overallExceptionRate: number;
  completionTrends: CompletionTrendData[];
  auditTypeDistribution: AuditTypeDistributionData[];
  completionPerformance: CompletionPerformanceData;
  exceptionStatistics: ExceptionStatisticsData;
  auditorPerformance: AuditorPerformanceData[];
  locationAnalysis: LocationAnalysisData[];
}

export interface CompletionTrendData {
  period: string; // e.g., "2024-01", "2024-W12"
  count: number;
  avgDuration: number;
  exceptionRate: number;
}

export interface AuditTypeDistributionData {
  type: AuditType;
  count: number;
  percentage: number;
}

export interface CompletionPerformanceData {
  onTime: number;
  aheadOfSchedule: number;
  completedLate: number;
  averageDelay: number; // in days
  averageEarly: number; // in days
}

export interface ExceptionStatisticsData {
  totalExceptions: number;
  overallRate: number;
  byType: Record<ExceptionType, number>;
  resolutionRate: number;
  averageResolutionTime: number; // in hours
}

export interface AuditorPerformanceData {
  userId: string;
  userName: string;
  userAvatar?: string;
  auditsCompleted: number;
  completionRate: number;
  averageAssetsPerDay: number;
  exceptionRate: number;
  performanceScore: number; // 0-100
}

export interface LocationAnalysisData {
  locationId: string;
  locationName: string;
  auditCount: number;
  percentage: number;
  totalAssets: number;
  exceptionRate: number;
}

// ============================================================================
// FILTERS & SEARCH
// ============================================================================

export interface AuditFilters {
  status?: AuditStatus[];
  statuses?: AuditStatus[]; // Alias for status used in some UI components
  priority?: AuditPriority[];
  priorities?: AuditPriority[]; // Alias for priority used in some UI components
  type?: AuditType[];
  types?: AuditType[]; // Alias for type used in some UI components
  assignedTo?: string[];
  assigneeIds?: string[]; // Alias for assignedTo used in some UI components
  assignedToUserIds?: string[]; // Alias for assignedTo used in some UI components
  createdBy?: string[];
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  completionPercentageMin?: number;
  completionPercentageMax?: number;
  hasExceptions?: boolean;
  isOverdue?: boolean;
  isRecurring?: boolean;
  recurringOnly?: boolean; // Filter for recurring audits only
  searchQuery?: string;
  search?: string; // Alias for searchQuery used in some UI components
}

export interface ExceptionFilters {
  exceptionType?: ExceptionType[];
  types?: ExceptionType[]; // Alias for exceptionType used in some UI components
  severity?: ExceptionSeverity[];
  severities?: ExceptionSeverity[]; // Alias for severity used in some UI components
  status?: ExceptionStatus[];
  statuses?: ExceptionStatus[]; // Alias for status used in some UI components
  priority?: ExceptionPriority[];
  priorities?: ExceptionPriority[]; // Alias for priority used in some UI components
  auditId?: string;
  auditIds?: string[]; // Alias for auditId used in some UI components
  assetId?: string;
  reportedBy?: string[];
  assignedTo?: string[];
  reportedDateFrom?: string;
  reportedDateTo?: string;
  requiresFollowUp?: boolean;
  hasImages?: boolean;
  searchQuery?: string;
  search?: string; // Alias for searchQuery used in some UI components
}

export interface ScheduledAuditFilters {
  type?: AuditType[];
  priority?: AuditPriority[];
  assignedTo?: string[];
  startDateFrom?: string;
  startDateTo?: string;
  recurringOnly?: boolean;
  searchQuery?: string;
}

export interface HistoryFilters {
  status?: ('Completed' | 'Cancelled')[];
  type?: AuditType[];
  priority?: AuditPriority[];
  assignedTo?: string[];
  completedDateFrom?: string;
  completedDateTo?: string;
  completionStatus?: ('OnTime' | 'Early' | 'Late')[];
  hasExceptions?: boolean;
  exceptionThreshold?: number;
  locationId?: string;
  searchQuery?: string;
}

// ============================================================================
// PAGINATION & SORTING
// ============================================================================

export interface PagedResult<T> {
  items: T[];
  data?: T[]; // Alias for items used in some UI components
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface SortOptions {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface PaginationOptions {
  page: number;
  pageSize: number;
}

// ============================================================================
// REQUEST/RESPONSE MODELS
// ============================================================================

export interface CreateAuditRequest {
  title: string;
  description?: string;
  type: AuditType;
  priority: AuditPriority;
  scope: AuditScope;
  scopeDetails: ScopeDetails;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  assignedUserIds: string[];
  isRecurring: boolean;
  recurrencePattern?: RecurrencePattern;
  settings?: {
    allowPartialCompletion?: boolean;
    requirePhotos?: boolean;
    requireNotes?: boolean;
    autoResolveExceptions?: boolean;
  };
  notificationSettings?: {
    sendEmail: boolean;
    sendPush: boolean;
    includeInstructions: boolean;
    customMessage?: string;
  };
}

export interface UpdateAuditRequest {
  title?: string;
  description?: string;
  priority?: AuditPriority;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  assignedUserIds?: string[];
  settings?: {
    allowPartialCompletion?: boolean;
    requirePhotos?: boolean;
    requireNotes?: boolean;
    autoResolveExceptions?: boolean;
  };
}

export interface ChangeAuditStatusRequest {
  status: AuditStatus;
  reason?: string;
  notifyAssignees?: boolean;
}

export interface RescheduleAuditRequest {
  startDate: string;
  endDate: string;
  reason?: string;
  notifyAssignees?: boolean;
}

export interface ResolveExceptionRequest {
  resolutionNotes: string;
  resolutionAction: string;
  createWorkOrder?: boolean;
  workOrderDetails?: {
    title: string;
    description: string;
    priority: string;
  };
}

export interface CreateExceptionRequest {
  auditId: string;
  assetId: string;
  exceptionType: ExceptionType;
  severity: ExceptionSeverity;
  priority: ExceptionPriority;
  field?: string;
  expectedValue?: string;
  actualValue?: string;
  expectedLocation?: string;
  actualLocation?: string;
  expectedCondition?: AssetCondition;
  actualCondition?: AssetCondition;
  notes?: string;
  requiresFollowUp: boolean;
  followUpDueDate?: string;
  assignedTo?: string;
}

export interface BulkAssignRequest {
  auditIds: string[];
  userIds: string[];
  notifyUsers?: boolean;
}

export interface BulkStatusChangeRequest {
  auditIds: string[];
  status: AuditStatus;
  reason?: string;
  notifyAssignees?: boolean;
}

export interface ExportAuditDataRequest {
  auditIds?: string[];
  filters?: AuditFilters;
  format: 'csv' | 'excel' | 'pdf';
  includeExceptions?: boolean;
  includeActivityLog?: boolean;
}

export interface ComparisonRequest {
  auditIds: string[]; // 2-5 audits
  metrics: string[]; // Which metrics to compare
}

export interface ComparisonReport {
  audits: Audit[];
  comparison: Record<string, unknown>;
  insights: string[];
}

// ============================================================================
// RECURRING AUDIT MANAGEMENT
// ============================================================================

export interface RecurringAuditSeries {
  seriesId: string;
  title: string;
  description?: string;
  type: AuditType;
  priority: AuditPriority;
  recurrencePattern: RecurrencePattern;
  scope: AuditScope;
  scopeDetails: ScopeDetails;
  assignedUserIds: string[];
  startedDate: string;
  nextOccurrenceDate?: string;
  isPaused: boolean;
  pausedUntil?: string;
  createdBy: string;
  createdDate: string;
}

export interface RecurringAuditOccurrence {
  occurrenceDate: string;
  auditId?: string;
  auditNumber?: string;
  status: 'Completed' | 'InProgress' | 'Scheduled' | 'Skipped';
}

export interface EditRecurringAuditOptions {
  scope: 'ThisOccurrence' | 'ThisAndFuture' | 'EntireSeries';
  changes: Partial<UpdateAuditRequest>;
}

export interface PauseSeriesRequest {
  pauseUntil?: string; // Specific date
  occurrences?: number; // Number of occurrences to skip
  indefinitely?: boolean;
}

export interface DeleteSeriesRequest {
  deleteFutureOnly: boolean;
}

// ============================================================================
// USER & WORKLOAD
// ============================================================================

export interface UserWorkload {
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  role: string;
  department: string;
  currentWorkload: number; // Number of active audits
  availability: 'Available' | 'Busy' | 'Heavy Workload' | 'On Leave';
  activeAudits: string[]; // Audit IDs
  completedThisMonth: number;
  averageCompletionTime: number; // in days
  performanceScore: number; // 0-100
}

export interface ConflictInfo {
  userId: string;
  userName: string;
  conflicts: {
    auditId: string;
    auditNumber: string;
    auditTitle: string;
    startDate: string;
    endDate: string;
  }[];
}

// ============================================================================
// ASSET AUDIT PROGRESS
// ============================================================================

export interface AssetAuditProgress {
  assetId: string;
  assetCode: string;
  assetName: string;
  location: string;
  category: string;
  status: 'Pending' | 'InProgress' | 'Completed' | 'Exception';
  auditedBy?: string;
  auditedByName?: string;
  auditedDate?: string;
  hasException: boolean;
  exceptionId?: string;
  notes?: string;
  images?: AuditImage[];
}

// ============================================================================
// TEMPLATES
// ============================================================================

export interface AuditTemplate {
  id: string;
  name: string;
  description?: string;
  type: AuditType;
  priority: AuditPriority;
  scope: AuditScope;
  scopeDetails: ScopeDetails;
  defaultDuration: number; // in days
  checklistItems?: string[]; // Checklist items for the template
  settings: {
    allowPartialCompletion: boolean;
    requirePhotos: boolean;
    requireNotes: boolean;
    autoResolveExceptions: boolean;
  };
  createdBy: string;
  createdDate: string;
  usageCount: number;
}

// ============================================================================
// VALIDATION SCHEMAS (for use with Zod or similar)
// ============================================================================

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}
